import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { canManageUsers, canManageSettings, getRoleLabel } from '../lib/utils';
import api from '../lib/api';
// EVENTS-PLUGIN SEAM: generic plugin nav registry
import { pluginNavItems } from '../plugins/registry';
import {
  LayoutDashboard,
  Truck,
  Users,
  Package,
  Layers,
  FileText,
  Settings,
  Search,
  LogOut,
  ChevronDown,
  Menu,
  X,
  Shield,
  ClipboardList,
  Scissors,
  CalendarDays,
  Tags,
  Palette,
  Trash2,
  ShieldCheck,
  Database,
  HardDrive,
  Barcode,
  FileSearch,
  Activity,
  ShoppingCart,
  ToggleLeft,
  ToggleRight,
  UserCog,
  Clock,
  ShieldAlert,
  AlertTriangle,
  HelpCircle,
  Undo2,
  RefreshCcw,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

const navItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard, roles: ['super_admin', 'admin', 'operator', 'read_only'] },
  { path: '/sales-order/new', label: 'Sales Order Entry', icon: ClipboardList, roles: ['super_admin', 'admin', 'operator'] },
  { path: '/planner', label: 'Order Pipeline', icon: CalendarDays, roles: ['super_admin', 'admin', 'operator', 'read_only'] },
  { path: '/dispatches', label: 'Dispatches', icon: Truck, roles: ['super_admin', 'admin', 'operator', 'read_only'] },
  { path: '/dispatch-view', label: 'Dispatch View', icon: FileText, roles: ['super_admin', 'admin', 'operator', 'read_only'] },
  { path: '/returns', label: 'Stock Returns', icon: Undo2, roles: ['super_admin', 'admin', 'operator', 'read_only'] },
  { path: '/dispatch/new', label: 'New Dispatch Note', icon: FileText, roles: ['super_admin', 'admin', 'operator'] },
  { path: '/customers', label: 'Customers', icon: Users, roles: ['super_admin', 'admin', 'operator', 'read_only'] },
  { path: '/suppliers', label: 'Suppliers', icon: Truck, roles: ['super_admin', 'admin', 'operator', 'read_only'] },
  { path: '/cobranding', label: 'Co-Branding', icon: Tags, roles: ['super_admin', 'admin', 'operator', 'read_only'] },
  { path: '/products', label: 'Products', icon: Package, roles: ['super_admin', 'admin', 'operator', 'read_only'] },
  { path: '/supplier-codes', label: 'Customer Product Codes', icon: Barcode, roles: ['super_admin', 'admin', 'operator', 'read_only'] },
  { path: '/supplier-trace', label: 'Customer Code Trace', icon: FileSearch, roles: ['super_admin', 'admin', 'operator', 'read_only'] },
  { path: '/batches', label: 'Batches', icon: Layers, roles: ['super_admin', 'admin', 'operator', 'read_only'] },
  { path: '/qc', label: 'QC Management', icon: ShieldCheck, roles: ['super_admin', 'admin'] },
  { path: '/templates', label: 'Template Designer', icon: Palette, roles: ['super_admin', 'admin'] },
  { path: '/reports', label: 'Reports', icon: FileText, roles: ['super_admin', 'admin', 'read_only'] },
  { path: '/help', label: 'User Guide', icon: HelpCircle, roles: ['super_admin', 'admin', 'operator', 'read_only', 'dpo'] },
];

const adminItems = [
  { path: '/users', label: 'User Management', icon: Shield, roles: ['super_admin', 'admin'] },
  { path: '/deleted-items', label: 'Deleted Items', icon: Trash2, roles: ['super_admin', 'admin'] },
  { path: '/admin/data-management', label: 'Data Management', icon: Database, roles: ['super_admin', 'admin'] },
  { path: '/backup', label: 'Backup & Restore', icon: HardDrive, roles: ['super_admin', 'admin'] },
  { path: '/admin/deployment', label: 'Deployment Health', icon: Activity, roles: ['super_admin', 'admin'] },
  { path: '/admin/compliance', label: 'Compliance Center', icon: ShieldCheck, roles: ['super_admin', 'admin', 'dpo'] },
  { path: '/admin/security-audit', label: 'Security Audit', icon: ShieldAlert, roles: ['super_admin', 'admin', 'dpo'] },
  { path: '/admin/recall', label: 'Recall Management', icon: AlertTriangle, roles: ['super_admin', 'admin', 'dpo'] },
  { path: '/settings', label: 'Settings', icon: Settings, roles: ['super_admin', 'admin', 'operator'] },  // Operator can access settings for password change
  { path: '/settings/accountsiq-sync', label: 'AccountsIQ Sync', icon: RefreshCcw, roles: ['super_admin', 'admin'], child: true },
];

export default function Layout({ children }) {
  const { user, logout, sessionWarning, keepAlive } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [featureToggles, setFeatureToggles] = useState({});
  const [mfaBannerDismissed, setMfaBannerDismissed] = useState(() => {
    return sessionStorage.getItem('mfa_banner_dismissed') === 'true';
  });
  const [operatorMode, setOperatorMode] = useState(() => {
    return localStorage.getItem('operatorMode') === 'true';
  });

  useEffect(() => {
    loadSettings();
    loadFeatureToggles();
    window.addEventListener('feature-toggles-changed', loadFeatureToggles);
    return () => window.removeEventListener('feature-toggles-changed', loadFeatureToggles);
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (error) {
      // Ignore - settings might not be accessible
    }
  };

  const loadFeatureToggles = async () => {
    try {
      const response = await api.get('/settings/feature-toggles');
      setFeatureToggles(response.data);
    } catch (error) {
      // Ignore
    }
  };

  const toggleOperatorMode = () => {
    const newVal = !operatorMode;
    setOperatorMode(newVal);
    localStorage.setItem('operatorMode', String(newVal));
    if (newVal) navigate('/quick-order');
  };

  // Operator mode nav items — streamlined for tablet operators
  const operatorModeNavItems = [
    { path: '/quick-order', label: 'Quick Order', icon: ShoppingCart, roles: ['super_admin', 'admin', 'operator'] },
    { path: '/planner', label: 'Order Pipeline', icon: CalendarDays, roles: ['super_admin', 'admin', 'operator'] },
    { path: '/account', label: 'My Account', icon: UserCog, roles: ['super_admin', 'admin', 'operator'] },
  ];

  // Dynamic nav items based on feature toggles
  const dynamicNavItems = featureToggles.transformations_enabled
    ? [...navItems.slice(0, navItems.findIndex(i => i.path === '/reports')),
       { path: '/transformations', label: 'Transformations', icon: Scissors, roles: ['super_admin', 'admin'] },
       ...navItems.slice(navItems.findIndex(i => i.path === '/reports'))]
    : navItems;

  // EVENTS-PLUGIN SEAM: inject plugin nav items after their `afterPath`, gated by flag.
  const navItemsWithPlugins = (() => {
    let items = [...dynamicNavItems];
    for (const n of pluginNavItems) {
      if (n.flagKey && !featureToggles[n.flagKey]) continue;
      const idx = items.findIndex((i) => i.path === n.afterPath);
      const at = idx >= 0 ? idx + 1 : items.length;
      items = [...items.slice(0, at), n, ...items.slice(at)];
    }
    return items;
  })();

  // Map paths to page permission keys
  const pathToPermissionKey = {
    '/': 'dashboard',
    '/sales-order/new': 'sales_order_entry',
    '/planner': 'order_pipeline',
    '/dispatches': 'dispatches',
    '/returns': 'stock_returns',
    '/dispatch-view': 'dispatch_view',
    '/dispatch/new': 'new_dispatch',
    '/customers': 'customers',
    '/cobranding': 'cobranding',
    '/products': 'products',
    '/supplier-codes': 'supplier_codes',
    '/supplier-trace': 'supplier_trace',
    '/batches': 'batches',
    '/qc': 'qc_management',
    '/templates': 'template_designer',
    '/reports': 'reports',
    '/users': 'user_management',
    '/deleted-items': 'deleted_items',
    '/admin/data-management': 'data_management',
    '/admin/deployment': 'deployment_health',
    '/admin/compliance': 'compliance_center',
    '/admin/security-audit': 'security_audit',
    '/admin/recall': 'recall_management',
    '/admin/data-management/label-designer': 'label_designer',
    '/admin/data-management/label-reprints': 'label_reprints',
    '/help': 'user_guide',
    '/backup': 'backup_restore',
    '/settings': 'settings',
    '/quick-order': 'quick_order',
    '/account': 'account',
  };

  // Check if user has access to a page based on role AND page_permissions
  const hasPageAccess = (item) => {
    // First check role-based access
    if (!item.roles.includes(user?.role)) return false;
    
    // Super admin and admin always have access (they can customize others' access)
    if (user?.role === 'super_admin' || user?.role === 'admin') return true;
    
    // For operators and read_only, check page_permissions if they exist
    const pagePermissions = user?.page_permissions || {};
    const permissionKey = pathToPermissionKey[item.path];
    
    // If no page_permissions set, allow access based on role (default behavior)
    if (Object.keys(pagePermissions).length === 0) return true;
    
    // If page_permissions exist, check if this page is allowed
    return permissionKey ? pagePermissions[permissionKey] === true : true;
  };

  const filteredNavItems = operatorMode
    ? operatorModeNavItems.filter(hasPageAccess)
    : navItemsWithPlugins.filter(hasPageAccess);
  const filteredAdminItems = operatorMode ? [] : adminItems.filter(hasPageAccess);

  // Pathname → User Guide section slug. Used to render a contextual "?" badge
  // next to the page title that deep-links straight to the relevant section.
  // Order matters — more-specific prefixes must come BEFORE shorter ones.
  const guideRouteMap = [
    { match: '/sales-order',                              slug: 'sales-order-entry' },
    { match: '/planner',                                  slug: 'order-pipeline' },
    { match: '/quick-order',                              slug: 'order-pipeline' },
    { match: '/dispatch-view',                            slug: 'dispatches' },
    { match: '/dispatches',                               slug: 'dispatches' },
    { match: '/dispatch',                                 slug: 'dispatches' },
    { match: '/customers',                                slug: 'customers' },
    { match: '/cobranding',                               slug: 'cobranding' },
    { match: '/products',                                 slug: 'products' },
    { match: '/supplier-codes',                           slug: 'customer-codes' },
    { match: '/supplier-trace',                           slug: 'customer-codes' },
    { match: '/batches',                                  slug: 'batches' },
    { match: '/qc',                                       slug: 'qc-management' },
    { match: '/templates',                                slug: 'template-designer' },
    { match: '/reports/compliance',                       slug: 'compliance-center' },
    { match: '/reports',                                  slug: 'reports' },
    { match: '/users',                                    slug: 'user-management' },
    { match: '/admin/data-management/accountsiq',         slug: 'accountsiq' },
    { match: '/settings/accountsiq-sync',                 slug: 'accountsiq' },
    { match: '/admin/data-management',                    slug: 'data-management' },
    { match: '/admin/compliance',                         slug: 'compliance-center' },
    { match: '/admin/security-audit',                     slug: 'security-audit' },
    { match: '/admin/recall',                             slug: 'recall-management' },
    { match: '/backup',                                   slug: 'backup-restore' },
    { match: '/pallet-labels',                            slug: 'template-designer' },
    { match: '/',                                         slug: 'getting-started' },
  ];
  const contextualGuideSlug = (() => {
    if (location.pathname === '/help' || location.pathname.startsWith('/help/')) return null;
    // Find the most-specific matching prefix
    const hit = guideRouteMap
      .filter(r => location.pathname === r.match || location.pathname.startsWith(r.match + '/') || (r.match === '/' && location.pathname === '/'))
      .sort((a, b) => b.match.length - a.match.length)[0];
    return hit ? hit.slug : null;
  })();

  return (
    <div className="flex h-screen overflow-hidden bg-zinc-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-zinc-900 text-white flex flex-col
        transform transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-zinc-800">
          <Link to="/" className="flex items-center gap-2 min-w-0">
            {settings?.company_logo ? (
              <img 
                src={settings.company_logo} 
                alt={settings?.company_name || 'Logo'} 
                className="h-8 w-auto max-w-[120px] object-contain"
              />
            ) : (
              <>
                <div className="w-8 h-8 bg-primary rounded flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">CB</span>
                </div>
                <span className="font-semibold text-sm truncate">
                  {settings?.company_name || 'Cashel Blue Ops'}
                </span>
              </>
            )}
          </Link>
          <button 
            className="lg:hidden p-1 hover:bg-zinc-800 rounded flex-shrink-0"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <div className="space-y-1 px-2">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`sidebar-item ${isActive ? 'active' : ''}`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                  <span className="truncate">{item.label}</span>
                </Link>
              );
            })}
          </div>

          {filteredAdminItems.length > 0 && (
            <>
              <div className="px-4 py-3 mt-4">
                <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  Administration
                </span>
              </div>
              <div className="space-y-1 px-2">
                {filteredAdminItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`sidebar-item ${isActive ? 'active' : ''} ${item.child ? 'pl-9 text-[13px]' : ''}`}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Icon className={`${item.child ? 'w-4 h-4' : 'w-5 h-5'} flex-shrink-0`} strokeWidth={1.5} />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </>
          )}
        </nav>

        {/* Operator Mode Toggle */}
        <div className="px-3 py-2 border-t border-zinc-800">
          <button
            onClick={toggleOperatorMode}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
              operatorMode 
                ? 'bg-primary/20 text-primary hover:bg-primary/30' 
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
            data-testid="btn-operator-mode-toggle"
          >
            {operatorMode ? <ToggleRight className="w-5 h-5 flex-shrink-0" /> : <ToggleLeft className="w-5 h-5 flex-shrink-0" />}
            <span className="truncate">Operator Mode</span>
          </button>
        </div>

        {/* User section */}
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-zinc-700 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-medium">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-zinc-400 truncate">{getRoleLabel(user?.role)}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className="h-14 sm:h-16 bg-white border-b border-border flex items-center justify-between px-3 sm:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <button 
              className="lg:hidden p-2 hover:bg-zinc-100 rounded flex-shrink-0"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="text-base sm:text-lg font-semibold text-foreground truncate">
              {filteredNavItems.find(i => i.path === location.pathname)?.label || 
               filteredAdminItems.find(i => i.path === location.pathname)?.label ||
               (settings?.company_name || 'Operations Hub')}
            </h1>
            {contextualGuideSlug && (
              <button
                type="button"
                onClick={() => navigate(`/help/${contextualGuideSlug}`)}
                title="Learn about this page in the User Guide"
                className="hidden sm:inline-flex items-center justify-center w-5 h-5 rounded-full border border-zinc-300 text-zinc-500 hover:border-indigo-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors text-[11px] font-semibold flex-shrink-0"
                data-testid="contextual-help-badge"
              >
                ?
              </button>
            )}
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/help')}
              className="h-9 w-9 text-zinc-500 hover:text-indigo-600 hover:bg-indigo-50"
              title="Open User Guide"
              data-testid="header-help-icon"
            >
              <HelpCircle className="w-5 h-5" />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4" data-testid="user-menu">
                  <span className="hidden sm:inline truncate max-w-[150px]">{user?.email}</span>
                  <span className="sm:hidden">{user?.name?.charAt(0).toUpperCase() || 'U'}</span>
                  <ChevronDown className="w-4 h-4 flex-shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                  Profile & Security
                </DropdownMenuItem>
                {canManageSettings(user?.role) && (
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                    Settings
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Session expiry warning banner */}
        {sessionWarning && (
          <div data-testid="session-warning-banner" className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>Your session will expire in 2 minutes due to inactivity.</span>
            </div>
            <button onClick={keepAlive} className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded text-xs font-medium" data-testid="keep-session-btn">
              Stay Logged In
            </button>
          </div>
        )}

        {/* MFA reminder banner — soft, dismissible per session */}
        {user && !user.mfa_enabled && !mfaBannerDismissed && (
          <div data-testid="mfa-reminder-banner" className={`${user.mfa_setup_pending ? 'bg-amber-600' : 'bg-sky-600'} text-white px-4 py-2 flex items-center justify-between text-sm`}>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              {user.mfa_setup_pending ? (
                <span><strong>MFA setup incomplete.</strong> Your previous setup was not verified. <button onClick={() => navigate('/profile')} className="underline font-medium">Complete it now</button> to secure your account.</span>
              ) : (
                <span>Two-factor authentication is not enabled on your account. <button onClick={() => navigate('/profile')} className="underline font-medium">Set it up now</button> for better security.</span>
              )}
            </div>
            <button
              onClick={() => { setMfaBannerDismissed(true); sessionStorage.setItem('mfa_banner_dismissed', 'true'); }}
              className="hover:bg-white/20 p-1 rounded"
              data-testid="dismiss-mfa-banner"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 p-3 sm:p-4 lg:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
